<script setup lang="ts">
import InputText from 'primevue/inputtext';
import { InputGroup, Typography } from '@apparatix/library/core';
import DetailEditorBase from './DetailEditorBase.vue';
import { useToastNotifications } from '@apparatix/composables';
import type { ContactErrors } from '@apparatix/schemas/Contact';
import { useContactStore } from '@apparatix/stores/useContactStore.ts';
import Button from 'primevue/button';
import type { OwnerSchema } from '@apparatix/schemas/Owner';
import Select from 'primevue/select';
import TrashIcon from '@primeicons/vue/trash';

type Props = {
  errors: ContactErrors;
}

const props = defineProps<Props>();
const store = useContactStore();
const { showWarning } = useToastNotifications();

const emit = defineEmits<{
  (e: 'on:add-row', type: string): void;
  (e: 'on:remove-row', type: string, rowIndex: number): void;
}>();

const leftColLabel = 'General';

const rightColLabel = 'Get in Touch';

const salutation = defineModel<string>('salutation');
const firstName = defineModel<string>('firstName');
const middleName = defineModel<string>('middleName');
const lastName = defineModel<string>('lastName');
const suffix = defineModel<string>('suffix');
const nickname = defineModel<string>('nickname');
const jobTitle = defineModel<string>('jobTitle');
const companyName = defineModel<string>('companyName');
const phoneNumbers = defineModel<any>('phoneNumbers');
const webPages = defineModel<any>('webPages');
const emails = defineModel<any>('emails');
const addresses = defineModel<any>('addresses');
const owner = defineModel<OwnerSchema>('owner');

if (store.invoiceFieldsLocked) {
  showWarning('You don\'t have permission to update certain locked fields on this Contact because it has ongoing invoices', 'Action Not Allowed', 6000);
}

</script>
<template>
  <DetailEditorBase :left-col-header="leftColLabel" :right-col-header="rightColLabel">
    <template #left-col>
      <Typography variant="form-label">
        Owner
      </Typography>
      <Select
        v-model="owner"
        :invalid="Boolean(props.errors?.owner)"
        :options="store.options.users"
        option-label="fullName"
        :placeholder="owner.fullName"
        fluid
      />
      <Typography v-if="Boolean(props.errors?.owner)" variant="form-error">
        {{ props.errors.owner }}
      </Typography>
      <Typography variant="form-label">
        Salutation
      </Typography>
      <InputText v-model="salutation" :invalid="Boolean(props.errors?.salutation)" fluid />
      <Typography v-if="Boolean(props.errors?.salutation)" variant="form-error">
        {{ props.errors.salutation }}
      </Typography>
      <Typography variant="form-label">
        First Name
      </Typography>
      <InputText v-model="firstName" :invalid="Boolean(props.errors?.firstName)" fluid />
      <Typography v-if="Boolean(props.errors?.firstName)" variant="form-error">
        {{ props.errors.firstName }}
      </Typography>
      <Typography variant="form-label">
        Middle Name
      </Typography>
      <InputText v-model="middleName" :invalid="Boolean(props.errors?.middleName)" fluid />
      <Typography v-if="Boolean(props.errors?.middleName)" variant="form-error">
        {{ props.errors.middleName }}
      </Typography>
      <Typography variant="form-label">
        Last Name
      </Typography>
      <InputText v-model="lastName" :invalid="Boolean(props.errors?.lastName)" fluid />
      <Typography v-if="Boolean(props.errors?.lastName)" variant="form-error">
        {{ props.errors.lastName }}
      </Typography>
      <Typography variant="form-label">
        Suffix
      </Typography>
      <InputText v-model="suffix" :invalid="Boolean(props.errors?.suffix)" fluid />
      <Typography v-if="Boolean(props.errors?.suffix)" variant="form-error">
        {{ props.errors.suffix }}
      </Typography>
      <Typography variant="form-label">
        Nickname
      </Typography>
      <InputText v-model="nickname" :invalid="Boolean(props.errors?.nickname)" fluid />
      <Typography v-if="Boolean(props.errors?.nickname)" variant="form-error">
        {{ props.errors.nickname }}
      </Typography>
      <Typography variant="form-label">
        Job Title
      </Typography>
      <InputText v-model="jobTitle" :invalid="Boolean(props.errors?.jobTitle)" fluid />
      <Typography v-if="Boolean(props.errors?.jobTitle)" variant="form-error">
        {{ props.errors.jobTitle }}
      </Typography>
      <Typography variant="form-label">
        Company Name
      </Typography>
      <InputText v-model="companyName" :invalid="Boolean(props.errors?.companyName)" fluid />
      <Typography v-if="Boolean(props.errors?.companyName)" variant="form-error">
        {{ props.errors.companyName }}
      </Typography>
    </template>
    <template #right-col>
      <InputGroup>
        <Typography variant="form-label">
          Emails
        </Typography>
        <template v-for="(email, idx) in emails" :key="email.id">
          <div class="flex items-center space-x-2">
            <InputText v-model="email.value.email" :disabled="store.invoiceFieldsLocked" fluid />
            <TrashIcon
              class="text-center"
              :class="{ 'cursor-not-allowed opacity-50': store.invoiceFieldsLocked, 'cursor-pointer': !store.invoiceFieldsLocked }"
              @click="store.invoiceFieldsLocked ? null : emit('on:remove-row', 'email', Number(idx))"
            />
          </div>
          <Typography v-if="Boolean(props.errors[`emails[${idx}].email`])" variant="form-error">
            {{ props.errors[`emails[${idx}].email`] }}
          </Typography>
        </template>
        <Button
          :disabled="store.invoiceFieldsLocked"
          @click="emit('on:add-row', 'email')"
        >
          Add Email
        </Button>
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Phone Numbers
        </Typography>
        <template v-for="(phoneNumber, idx) in phoneNumbers" :key="phoneNumber.id">
          <div class="flex items-center space-x-2">
            <InputText v-model="phoneNumber.value.number" :disabled="store.invoiceFieldsLocked" fluid />
            <Select
              v-model="phoneNumber.value.addressType"
              :disabled="store.invoiceFieldsLocked"
              :options="store.options.phoneNumberTypes"
            />
            <TrashIcon
              class="text-center"
              :class="{ 'cursor-not-allowed opacity-50': store.invoiceFieldsLocked, 'cursor-pointer': !store.invoiceFieldsLocked }"
              @click="store.invoiceFieldsLocked ? null : emit('on:remove-row', 'phone', Number(idx))"
            />
          </div>
          <Typography v-if="Boolean(props.errors[`phoneNumbers[${idx}].number`])" variant="form-error">
            {{ props.errors[`phoneNumbers[${idx}].number`] }}
          </Typography>
        </template>
        <Button
          :disabled="store.invoiceFieldsLocked"
          @click="emit('on:add-row', 'phone')"
        >
          Add Phone Number
        </Button>
      </InputGroup>
      <InputGroup>
        <Typography variant="form-label">
          Websites
        </Typography>
        <template v-for="(webPage, idx) in webPages" :key="webPage.id">
          <div class="flex items-center space-x-2">
            <InputText v-model="webPage.value.url" fluid />
            <Select
              v-model="webPage.value.addressType"
              :options="store.options.webPageOptions"
            />
            <TrashIcon class="text-center cursor-pointer" @click="emit('on:remove-row', 'webPage', Number(idx))" />
          </div>
          <Typography v-if="Boolean(props.errors[`webPages[${idx}].url`])" variant="form-error">
            {{ props.errors[`webPages[${idx}].url`] }}
          </Typography>
        </template>
        <Button
          @click="emit('on:add-row', 'webPage')"
        >
          Add Website
        </Button>
      </InputGroup>
      <div class="flex items-center w-full pb-3 lg:pb-4 border-b border-[var(--p-content-border-color)]">
        <Typography variant="form-heading">
          Location
        </Typography>
      </div>
      <InputGroup>
        <Typography variant="form-label">
          Address
        </Typography>
        <InputText
          v-model="addresses[0].addressLine1"
          :disabled="store.invoiceFieldsLocked"
          placeholder="Address Line 1"
          fluid
        />
        <InputText
          v-model="addresses[0].addressLine2"
          :disabled="store.invoiceFieldsLocked"
          placeholder="Address Line 2"
          fluid
        />
        <InputText
          v-model="addresses[0].city"
          :disabled="store.invoiceFieldsLocked"
          placeholder="City"
          fluid
        />
        <InputText
          v-model="addresses[0].region"
          :disabled="store.invoiceFieldsLocked"
          placeholder="State"
          fluid
        />
        <InputText
          v-model="addresses[0].postalCode"
          :disabled="store.invoiceFieldsLocked"
          placeholder="Postal Code"
          fluid
        />
      </InputGroup>
    </template>
  </DetailEditorBase>
</template>
